import { useEsiteCtx } from 'services/esite/context';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDealerCustomerQuery } from 'services/dealer-customer/use-query-dealer-customer';
import { isEqual } from 'lodash';
import { dealerCustomerPayload } from 'services/dealer-customer/types';

export const useDealerCustomer = (dealerStoreId = null) => {
  const { storeId, locale } = useEsiteCtx();
  const [formData, setFormData] = useState({
    customerNumber: '',
    storeId: ''
  });
  const { data, isFetched, isRefetching, isError, refetch, isLoading, isFetching } =
    useDealerCustomerQuery({
      data: formData,
      storeId: dealerStoreId ?? storeId,
      locale
    });
  const queryClient = useQueryClient();

  // * checks if required fields are filled
  const fieldsAreFilled = (data: unknown) => {
    return Object.values(data).some((value) => value !== '');
  };

  // remove method doest exist anymore so we have to implement removeQuereries method from queryClient
  // to have the same behavior as remove

  const handleRemoveQuery = () => {
    const queryCache = queryClient.getQueryCache();

    //Getting the active keys and looking for dealerCustomer key
    const queryKeys = queryCache.getAll().map((cache) => cache.queryKey);
    const dealerCustomerKey = queryKeys.find((Key) =>
      Key.includes('dealerCustomer')
    );

    queryClient.removeQueries({ queryKey: dealerCustomerKey });
  };

  // * sets payload data
  const handleSearch = (data: dealerCustomerPayload) => {
    // * remove previous query state
    handleRemoveQuery();

    // * manual refetch since useEffect doesn't run when state is the same
    const isSameState = isEqual(formData, data);
    const someFieldsAreFilled = fieldsAreFilled(data);

    if (isSameState && someFieldsAreFilled) {
      refetch();
    } else {
      setFormData(data);
    }
  };

  // * waits for payload state to be setted to refetch data
  useEffect(() => {
    const someFieldsAreFilled = fieldsAreFilled(formData);
    if (someFieldsAreFilled) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  return {
    data,
    startSearch: handleSearch,
    stores: data?.dealerStoreLocations?.stores ?? [],
    orderTypeInfo: data?.orderTypesInfo?.orderTypes ?? [],
    isLoading,
    isFetched,
    isRefetching,
    isError,
    isFetching,
    remove: handleRemoveQuery
  };
};
